import { SignIn, useUser, useOrganization } from '@clerk/clerk-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { useConversationContext } from '../../contexts/ConversationContext'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/stores/app'
import { useAtlasChatDismiss } from '@/hooks/useAtlasChatDismiss'
import { useViewportChatSidebarClamp } from '@/hooks/useViewportChatSidebarClamp'
import { agentDisplayName } from '../../config/env'
import { AppSidebar, type AppSidebarHandle } from '@/components/AppSidebar'
import { CommandPalette } from '@/components/CommandPalette/CommandPalette'
import { MobileBottomNav } from '@/components/layout/MobileBottomNav'
import { ChatSidebarHost } from '@/components/layout/ChatSidebarHost'
import { ContentPanelArea } from '@/components/layout/ContentPanelArea'

interface AuthLayoutProps {
  chatComponent: ReactNode
}

export function AuthLayout({ chatComponent }: AuthLayoutProps) {
  const chatSidebarOpen = useAppStore((s) => s.chatSidebar.open)
  const chatSidebarWidth = useAppStore((s) => s.chatSidebar.width)
  const setChatSidebarOpen = useAppStore((s) => s.setChatSidebarOpen)
  const [rightSidebarMobileOpen, setRightSidebarMobileOpen] = useState(false)
  // With no open tabs the content area renders the Home view full-bleed, which
  // intentionally shows the "strong" glow across the entire shell (behind the
  // rails too). With tabs open the shell stays on the light base gradient.
  const hasNoTabs = useAppStore((s) => s.tabs.items.length === 0)

  useViewportChatSidebarClamp()

  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < 768
  })
  const { isSignedIn, isLoaded } = useUser()
  const { organization, isLoaded: orgLoaded } = useOrganization()
  const { agentId } = useConversationContext()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Refs for mobile navigation handlers
  const appSidebarRef = useRef<AppSidebarHandle>(null)
  const conversationSidebarOpenRef = useRef<(open: boolean) => void>(() => {})
  const mobilePanel = searchParams.get('mobilePanel')

  // Handle organization changes - navigate to base route when org changes
  const prevOrgIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const orgId = organization?.id
    if (!orgId) return

    // When organization actually changes, navigate to base route and reset
    const prev = prevOrgIdRef.current
    if (prev && prev !== orgId) {
      navigate(`/${agentId}`, { replace: true, state: { reset: true } })
    }

    prevOrgIdRef.current = orgId
  }, [organization?.id, agentId, navigate])

  // The persisted chatSidebar open state is the only source of truth: nothing
  // auto-opens the panel on a new conversation, so a deep link arrives with the
  // sidebar closed and stays closed. SidebarTrigger is always visible on
  // desktop, so users can re-open at any time.

  useEffect(() => {
    const update = () => {
      setIsMobileViewport(window.innerWidth < 768)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const setMobilePanel = useCallback(
    (panel: 'chat' | 'menu') => {
      setSearchParams(
        (p) => {
          p.set('mobilePanel', panel)
          return p
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (!isMobileViewport) return

    if (mobilePanel === 'chat') {
      setRightSidebarMobileOpen(true)
      appSidebarRef.current?.closeDrawer()
    } else if (mobilePanel === 'menu') {
      setRightSidebarMobileOpen(false)
      appSidebarRef.current?.openDrawer()
    }
  }, [isMobileViewport, mobilePanel])

  useEffect(() => {
    if (isMobileViewport || !mobilePanel) return
    // Cleanup: remove mobile-only panel state when leaving mobile viewport.
    setSearchParams(
      (p) => {
        p.delete('mobilePanel')
        return p
      },
      { replace: true }
    )
  }, [isMobileViewport, mobilePanel, setSearchParams])

  // Dismiss the Atlas chat window on outside click or Escape
  useAtlasChatDismiss()

  // The only geometry value JS still owns. Everything downstream of it — the
  // grid columns, the sheet backdrop inset, the panel width — is CSS math on
  // this var, so a drag re-renders THIS component only.
  const shellStyle = useMemo(
    () => ({ '--chat-width': `${chatSidebarWidth}px` }) as CSSProperties,
    [chatSidebarWidth]
  )

  if (!isLoaded || !orgLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (!isSignedIn) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Sign in to {agentDisplayName(agentId)}
            </h2>
          </div>
          <SignIn />
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Mounted outside the tab tree on purpose: a dialog rendered inside a
          tab is stamped with that tab's route and hidden when the user
          switches away. */}
      <CommandPalette />
      <div
        data-slot="app-shell"
        data-chat-open={chatSidebarOpen ? 'true' : 'false'}
        style={shellStyle}
        className={cn(
          // h-full, not h-screen: html/body/#root carry the height chain and
          // overflow:clip (see index.css), so the shell has to match body or
          // it overshoots the visual viewport on iOS Safari.
          'h-full relative grid grid-rows-1 grid-cols-1',
          // One expression, written once, reused by the chat column and the
          // sheet backdrop: the persisted width, capped so the centre column
          // can never fall below --content-min-width at the inline tier.
          // 100%, not 100vw — 100vw includes the scrollbar gutter, so the cap
          // would overshoot by the scrollbar width. No px floor on the result:
          // under a pathological root font size the centre column wins and the
          // chat column collapses, which is the safer of the two failures.
          '[--chat-inline-width:min(var(--chat-width),calc(100%_-_var(--app-sidebar-width)_-_var(--content-min-width)))]',
          // ── Shell geometry ─────────────────────────────────────────────
          // <768  single column; the rail is a drawer and the chat panel is a
          //       full-screen sheet, so neither reserves a track.
          // 768+  rail | content | chat. The chat panel is `fixed` here, so
          //       the third track only reserves the 48px CLOSED rail; when it
          //       is open the track collapses to 0 and the panel OVERLAYS the
          //       content instead of squeezing it.
          // 1280+ the chat panel becomes a real grid child (see
          //       ConversationSidebar) and the third track carries its width.
          'md:grid-cols-[var(--app-sidebar-width)_minmax(0,1fr)_var(--app-rail-width)]',
          'md:data-[chat-open=true]:grid-cols-[var(--app-sidebar-width)_minmax(0,1fr)_0px]',
          'xl:data-[chat-open=true]:grid-cols-[var(--app-sidebar-width)_minmax(0,1fr)_var(--chat-inline-width)]',
          // Right inset of the localized sheet backdrop/seam strips (read as
          // `var(--sheet-overlay-right)` in sheet.tsx). It stops at the chat
          // panel only where the panel is a real inline column (xl). Below xl
          // the panel merely floats, so a modal sheet owns the whole viewport:
          // the dim runs edge to edge and suspends the chat overlay under it.
          '[--sheet-overlay-right:0px]',
          'xl:[--sheet-overlay-right:var(--app-rail-width)]',
          'xl:data-[chat-open=true]:[--sheet-overlay-right:var(--chat-inline-width)]',
          hasNoTabs ? 'bg-app-gradient-strong' : 'bg-app-gradient'
        )}>
        {/* Left sidebar — absolute overlay (encapsulates its own
            SidebarProvider). It floats over the first grid track, which
            reserves the rail (--app-sidebar-width) so expanding it never reflows content. */}
        <AppSidebar ref={appSidebarRef} />

        {/* Main content area — grid column 2. Width comes entirely from the
            track (`minmax(0,1fr)`); nothing here measures the chat panel.
            Below md the bottom nav is fixed over this column, so reserve its
            height (plus the home-indicator inset) or the last row of every
            list sits under it. */}
        <div className="flex flex-col min-h-0 min-w-0 md:col-start-2 pb-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom))] md:pb-0">
          <ContentPanelArea />
        </div>

        {/* Right sidebar - agent conversation */}
        <ChatSidebarHost
          chatComponent={chatComponent}
          openRef={conversationSidebarOpenRef}
          mobileOpen={rightSidebarMobileOpen}
          onMobileChange={setRightSidebarMobileOpen}
          open={chatSidebarOpen}
          onOpenChange={setChatSidebarOpen}
        />
      </div>

      {/* Mobile bottom navigation */}
      <MobileBottomNav
        onMenuClick={() => {
          setMobilePanel('menu')
          setRightSidebarMobileOpen(false)
          appSidebarRef.current?.openDrawer()
        }}
        onChatClick={() => {
          setMobilePanel('chat')
          appSidebarRef.current?.closeDrawer()
          setRightSidebarMobileOpen(true)
          conversationSidebarOpenRef.current(true)
        }}
      />
    </>
  )
}
